import { ROUTES } from 'constants/routes';

export type AskGuruEnrollmentRef = {
  batchId: string;
  schoolSlug: string;
  courseId?: string | null;
  enrollmentStatus?: 'active' | 'inactive';
  isPastEnrollment?: boolean;
};

const SCHOOL_LEARN_ROUTES: { prefix: string; slug: string }[] = [
  { prefix: ROUTES.PARASHARI_LEARN, slug: 'parashari' },
  { prefix: ROUTES.JAIMINI_LEARN, slug: 'jaimini' },
  { prefix: ROUTES.BNN_LEARN, slug: 'bnn' },
  { prefix: ROUTES.KP_LEARN, slug: 'kp' },
  { prefix: ROUTES.LAL_KITAB_LEARN, slug: 'lal-kitab' },
  { prefix: ROUTES.SWARA_VIGYAN_LEARN, slug: 'swara-vigyan' },
  { prefix: ROUTES.SURYA_SIDDHANTA_LEARN, slug: 'surya-siddhanta' },
];

function batchIdFromPath(pathname: string): string | null {
  const guruPath = pathname.match(/^\/batches\/([^/]+)\/path\//);
  if (guruPath?.[1]) return guruPath[1];
  const batchDetail = pathname.match(/^\/batches\/([^/]+)/);
  if (batchDetail?.[1] && batchDetail[1] !== 'path') return batchDetail[1];
  return null;
}

function courseIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/courses\/([^/]+)/);
  return match?.[1] ?? null;
}

function schoolSlugFromLearnPath(pathname: string): string | null {
  for (const { prefix, slug } of SCHOOL_LEARN_ROUTES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return slug;
  }
  return null;
}

export function isActiveLearnerEnrollment(enrollment: AskGuruEnrollmentRef): boolean {
  return enrollment.enrollmentStatus === 'active' && !enrollment.isPastEnrollment;
}

export function filterActiveLearnerEnrollments(
  enrollments: AskGuruEnrollmentRef[]
): AskGuruEnrollmentRef[] {
  return enrollments.filter(isActiveLearnerEnrollment);
}

export function hasAskGuruEnrollmentForSchool(
  schoolSlug: string,
  enrollments: AskGuruEnrollmentRef[]
): boolean {
  return filterActiveLearnerEnrollments(enrollments).some((e) => e.schoolSlug === schoolSlug);
}

/** True when the learner has an active batch enrollment matching the current route context. */
export function hasAskGuruEnrollmentForPath(
  pathname: string,
  enrollments: AskGuruEnrollmentRef[]
): boolean {
  const active = filterActiveLearnerEnrollments(enrollments);
  if (active.length === 0) return false;

  const batchId = batchIdFromPath(pathname);
  if (batchId) {
    return active.some((e) => e.batchId === batchId);
  }

  const courseId = courseIdFromPath(pathname);
  if (courseId) {
    return active.some((e) => e.courseId === courseId);
  }

  const schoolSlug = schoolSlugFromLearnPath(pathname);
  if (schoolSlug) {
    return active.some((e) => e.schoolSlug === schoolSlug);
  }

  if (pathname === ROUTES.BATCHES) {
    return true;
  }

  return false;
}
